import { useEffect, useState } from "react";

/**
 * Three first-class themes, not two.
 *
 *   "light" — warm paper + Sikhio indigo. The daytime briefing instrument.
 *             Municipal staff and the Mayor, in lit rooms and on projectors.
 *   "dark"  — near-black + lifted indigo (lavender). The night watch. Low
 *             light emission, still the same identity hue.
 *   "a11y"  — pure black + warm ochre, AAA ink ladder, no short-
 *             wavelength blue. For low-vision and colour-blind readers,
 *             and safe to leave open at 03:00 during a flood.
 *
 * Axiom Design Core §Layer 5 requires the colour-blind-safe mode to be a
 * first-class toggle, not an accessibility afterthought. That is why it
 * lives here rather than behind a `prefers-contrast` media query the
 * reader has to already know to look for.
 */
export type Theme = "light" | "dark" | "a11y";

const STORAGE_KEY = "sikhio:theme";

const THEMES: readonly Theme[] = ["light", "dark", "a11y"] as const;

function isTheme(v: string | null): v is Theme {
  return v === "light" || v === "dark" || v === "a11y";
}

function readSavedTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return isTheme(v) ? v : null;
}

function defaultTheme(): Theme {
  // "Museum white" is the product default. This is a daytime briefing screen
  // read by municipal staff and shown to the Mayor in lit rooms and on
  // projectors, where a near-black sheet is the wrong instrument. Dark
  // and a11y remain available as explicit toggles.
  //
  // This must stay in sync with the pre-hydration boot script in index.html —
  // if they disagree, the page flashes and then flips on hydration.
  return "light";
}

/** Thai label for the theme button, so the toggle says what it does. */
export const THEME_LABEL: Record<Theme, { glyph: string; th: string; en: string }> = {
  light: { glyph: "☀", th: "สว่าง", en: "light" },
  dark:  { glyph: "☾", th: "มืด", en: "dark" },
  a11y:  { glyph: "◐", th: "ความคมชัด", en: "high contrast" },
};

export function useTheme(): {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
  next: Theme;
} {
  const [theme, setThemeState] = useState<Theme>(() => readSavedTheme() ?? defaultTheme());

  // Push to <html data-theme="..."> on every change
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    // a11y is a dark ground — telling the UA keeps form controls and
    // scrollbars dark instead of the OS picking a light palette.
    root.style.colorScheme = theme === "light" ? "light" : "dark";
  }, [theme]);

  const setTheme = (t: Theme) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // Safari private mode may throw
    }
    setThemeState(t);
  };

  const next = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length];

  return {
    theme,
    setTheme,
    next,
    toggle: () => setTheme(next),
  };
}
